import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { mindfulnessService } from '../../../services/mindfulness.service';
import './ShareMindfulnessModal.css';

const ShareMindfulnessModal = ({ isOpen, onClose, onShare, userId }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && userId) {
      loadSessions();
    }
  }, [isOpen, userId]);

  const loadSessions = async () => {
    try {
      setIsLoading(true);
      const userSessions = await mindfulnessService.getSessions(userId);
      setSessions(userSessions);
    } catch (error) {
      console.error('Error loading mindfulness sessions:', error);
      setError('Failed to load mindfulness sessions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = () => {
    if (!selectedSession) return;
    onShare(selectedSession);
  };

  const formatDuration = (minutes) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }
    return `${minutes}m`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="modal-content"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={e => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>Share Mindfulness Session</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          
          <div className="modal-body">
            {isLoading ? (
              <div className="loading-state">Loading mindfulness sessions...</div>
            ) : error ? (
              <div className="error-state">{error}</div>
            ) : sessions.length === 0 ? (
              <div className="empty-state">
                <p>You haven't completed any mindfulness sessions yet.</p>
                <p>Complete a session to share it with friends!</p>
              </div>
            ) : (
              <div className="sessions-list">
                {sessions.map(session => (
                  <motion.div
                    key={session.id}
                    className={`session-item ${selectedSession?.id === session.id ? 'selected' : ''}`}
                    onClick={() => setSelectedSession(session)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="session-info">
                      <h3>{session.type}</h3>
                      <div className="session-meta">
                        <span className="session-duration">
                          ⏱️ {formatDuration(session.duration)}
                        </span>
                        <span className="session-date">
                          📅 {new Date(session.completedAt).toLocaleDateString()}
                        </span>
                      </div>
                      {session.mood && (
                        <div className="session-mood">
                          Mood after: {session.mood}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button 
              className="cancel-button" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="share-button"
              onClick={handleShare}
              disabled={!selectedSession || isLoading}
            >
              Share Session
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ShareMindfulnessModal; 